/**
 * ToastContainer Component
 * Manages and displays multiple toast notifications
 * 
 * Requirements: 1.5
 */

import { useState, useCallback } from 'react';
import Toast, { ToastMessage, ToastType } from './Toast';
import './ToastContainer.css';

interface ToastContainerProps {
  position?: 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center';
}

export interface ToastOptions {
  type?: ToastType;
  message: string;
  troubleshooting?: string[];
  duration?: number;
}

// Singleton instance for global toast management
let toastManagerInstance: ToastManager | null = null;

export class ToastManager {
  private listeners: Set<(messages: ToastMessage[]) => void> = new Set();
  private messages: ToastMessage[] = [];
  private nextId: number = 1;

  static getInstance(): ToastManager {
    if (!toastManagerInstance) {
      toastManagerInstance = new ToastManager();
    }
    return toastManagerInstance;
  }

  subscribe(listener: (messages: ToastMessage[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.messages);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(listener => listener([...this.messages]));
  }

  show(options: ToastOptions): string {
    const id = `toast-${this.nextId++}`;
    const message: ToastMessage = {
      id,
      type: options.type || 'info',
      message: options.message,
      troubleshooting: options.troubleshooting,
      duration: options.duration,
    };

    this.messages.push(message);
    this.notify();
    
    return id;
  }

  error(message: string, troubleshooting?: string[], duration?: number): string {
    return this.show({ type: 'error', message, troubleshooting, duration });
  }

  warning(message: string, troubleshooting?: string[], duration?: number): string {
    return this.show({ type: 'warning', message, troubleshooting, duration });
  }

  success(message: string, duration?: number): string {
    return this.show({ type: 'success', message, duration });
  }

  info(message: string, duration?: number): string {
    return this.show({ type: 'info', message, duration });
  }

  dismiss(id: string): void {
    this.messages = this.messages.filter(msg => msg.id !== id);
    this.notify();
  }

  dismissAll(): void {
    this.messages = [];
    this.notify();
  }
}

function ToastContainer({ position = 'top-right' }: ToastContainerProps) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const toastManager = ToastManager.getInstance();

  // Subscribe to toast manager
  useState(() => {
    const unsubscribe = toastManager.subscribe(setMessages);
    return unsubscribe;
  });

  const handleClose = useCallback((id: string) => {
    toastManager.dismiss(id);
  }, [toastManager]);

  if (messages.length === 0) {
    return null;
  }

  return (
    <div className={`toast-container toast-container-${position}`}>
      {messages.map(message => (
        <Toast key={message.id} message={message} onClose={handleClose} />
      ))}
    </div>
  );
}

export default ToastContainer;
